import React, { useState, useEffect, lazy, Suspense } from 'react';
import SplashScreen from './components/SplashScreen';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import AuthContext from './context/AuthContext';
import { supabase } from './lib/supabase';
import { subscribeToPush } from './utils/pushNotifications';

// Auth (small, keep eager)
import RoleSelection    from './pages/auth/RoleSelection';
import CashierAuth      from './pages/auth/CashierAuth';
import CustomerAuth     from './pages/auth/CustomerAuth';
import Welcome          from './pages/misc/Welcome';
import VerifyOTP        from './pages/auth/VerifyOTP';
import ForgotPassword   from './pages/auth/ForgotPassword';
import ResetPassword    from './pages/auth/ResetPassword';

// Admin (lazy)
const AdminAuth       = lazy(() => import('./pages/admin/AdminAuth'));
const AdminDashboard  = lazy(() => import('./pages/admin/AdminDashboard'));
const PaymentsPage    = lazy(() => import('./pages/admin/PaymentsPage'));
const FeedbackPage    = lazy(() => import('./pages/admin/FeedbackPage'));
const AccountsPage    = lazy(() => import('./pages/admin/AccountsPage'));
const AdminLayout     = lazy(() => import('./pages/admin/AdminLayout'));
const DashboardPage   = lazy(() => import('./pages/admin/DashboardPage'));
const CustomersPage   = lazy(() => import('./pages/admin/CustomersPage'));
const ReportsPage     = lazy(() => import('./pages/admin/ReportsPage'));
const ActivityPage    = lazy(() => import('./pages/admin/ActivityPage'));

// Cashier (lazy)
const Dashboard          = lazy(() => import('./pages/dashboard/Dashboard'));
const Debtors            = lazy(() => import('./pages/debtors/Debtors'));
const DebtorDetail       = lazy(() => import('./pages/debtors/DebtorDetail'));
const DebtorHistory      = lazy(() => import('./pages/debtors/DebtorHistory'));
const DebtorPayments     = lazy(() => import('./pages/debtors/DebtorPayments'));
const MonthEnd           = lazy(() => import('./pages/debtors/MonthEnd'));
const AddDebtor          = lazy(() => import('./pages/debtors/AddDebtor'));
const AddCharge          = lazy(() => import('./pages/debtors/AddCharge'));
const RecordPayment      = lazy(() => import('./pages/payments/RecordPayment'));
const Invite             = lazy(() => import('./pages/misc/Invite'));
const Settings           = lazy(() => import('./pages/misc/Settings'));
const QRPage             = lazy(() => import('./pages/misc/QRPage'));
const TransactionHistory = lazy(() => import('./pages/misc/TransactionHistory'));
const TermsAndConditions = lazy(() => import('./pages/misc/TermsAndConditions'));
const PrivacyPolicy      = lazy(() => import('./pages/misc/PrivacyPolicy'));
const JoinApp            = lazy(() => import('./pages/misc/JoinApp'));
const Profile            = lazy(() => import('./pages/misc/Profile'));
const Notifications      = lazy(() => import('./pages/misc/Notifications'));

// Legal pages
const WebPrivacyPolicy  = lazy(() => import('./pages/legal/PrivacyPage'));
const WebTermsOfService = lazy(() => import('./pages/legal/TermsPage'));
const WebAbout          = lazy(() => import('./pages/legal/AboutPage'));
const WebContact        = lazy(() => import('./pages/legal/ContactPage'));
const DeleteAccount     = lazy(() => import('./pages/legal/DeleteAccountPage'));

// Customer (lazy)
const CustomerDashboard     = lazy(() => import('./pages/customer/CustomerDashboard'));
const RestaurantView        = lazy(() => import('./pages/customer/RestaurantView'));
const Scan                  = lazy(() => import('./pages/customer/Scan'));
const CustomerHistory       = lazy(() => import('./pages/customer/CustomerHistory'));
const CustomerPayments      = lazy(() => import('./pages/customer/CustomerPayments'));
const CustomerProfile       = lazy(() => import('./pages/customer/CustomerProfile'));
const CustomerDispute       = lazy(() => import('./pages/customer/CustomerDispute'));
const CustomerSettings      = lazy(() => import('./pages/customer/CustomerSettings'));
const CustomerNotifications = lazy(() => import('./pages/customer/CustomerNotifications'));
const CustomerInvite        = lazy(() => import('./pages/customer/CustomerInvite'));

const PageSpinner = () => (
  <div className="flex h-screen items-center justify-center bg-[#0a1628]">
    <div className="w-6 h-6 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
  </div>
);

// Route guards are evaluated only after App's own `if (loading) return
// <PageSpinner />` early-return has already passed, so by the time any of
// these run, `profile` being null can only mean the fetch already
// finished and failed (network hiccup, transient DB error, etc.) — never
// "still in progress." The old `profile && profile.role !== 'x'` check
// treated that failed-and-null case as pass-through instead of blocking
// it, so a broken profile fetch would render deep inside the wrong
// role's dashboard shell instead of bouncing out. Fail closed: no
// profile is treated the same as the wrong role.
function OwnerRoute({ session, profile, children }) {
  if (!session) return <Navigate to="/cashier/login" replace />;
  if (!profile || profile.role !== 'owner') return <Navigate to="/" replace />;
  return children;
}

function CustomerRoute({ session, profile, children }) {
  if (!session) return <Navigate to="/customer/login" replace />;
  if (!profile || profile.role !== 'customer') return <Navigate to="/" replace />;
  return children;
}

function AdminRoute({ session, profile, children }) {
  if (!session) return <Navigate to="/admin/login" replace />;
  if (!profile || profile.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

function App() {
  const [loading, setLoading] = useState(true);
  const [splash, setSplash]   = useState(true);
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [suspendedNotice, setSuspendedNotice] = useState(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) fetchProfile(session.user.id);
      else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // ── FIXED: subscribeToPush is now inside the function ──────────────────
  const fetchProfile = async (userId) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, role, business_name, full_name, email, status, code, owner_id, phone, fee, fee_status')
      .eq('id', userId)
      .single();

    if (error) console.error('Profile fetch failed:', error);

    // Suspended accounts must never keep a live session. This is the one
    // choke point every session passes through — a fresh login AND a
    // session restored on app reload/reopen — unlike the per-form checks
    // in CashierAuth/CustomerAuth, which only run once at login submit and
    // never re-fire if an account is suspended *while* already logged in,
    // or the app is reopened with a still-valid saved session. Route
    // guards (OwnerRoute/CustomerRoute/AdminRoute) only ever checked role,
    // never status, so without this, a suspended user kept full access
    // until their token happened to expire.
    if (data?.status === 'suspended') {
      setSuspendedNotice(true);
      await supabase.auth.signOut();
      setProfile(null);
      setLoading(false);
      return;
    }

    setProfile(data || null);
    setLoading(false);

    if (data) {
      subscribeToPush(userId);
    }
  };

  if (splash) return <SplashScreen onDone={() => setSplash(false)} />;
  if (loading) return <PageSpinner />;

  const HomeRoute = () => {
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;

    if (session && profile) {
      if (profile.role === 'owner')    return <Navigate to="/dashboard" replace />;
      if (profile.role === 'customer') return <Navigate to="/customer/dashboard" replace />;
      if (profile.role === 'admin')    return <Navigate to="/admin/dashboard" replace />;
    }

    if (isInstalled) return <RoleSelection />;
    return <Navigate to="/welcome" replace />;
  };

  return (
    <AuthContext.Provider value={{ user: session?.user ?? null, profile }}>
    <ThemeProvider>
      <Router>
        {suspendedNotice && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
            background: '#e53535', color: '#fff', textAlign: 'center',
            padding: '10px 16px', fontSize: 13, fontWeight: 700,
          }}>
            Your account has been suspended. Please contact support.
            <button
              onClick={() => setSuspendedNotice(false)}
              style={{ marginLeft: 12, background: 'none', border: 'none', color: '#fff', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}
            >
              Dismiss
            </button>
          </div>
        )}
        <Suspense fallback={<PageSpinner />}>
          <Routes>
            {/* Entry Point */}
            <Route path="/" element={<HomeRoute />} />

            {/* Auth */}
            <Route path="/cashier/login"    element={<CashierAuth />} />
            <Route path="/cashier/register" element={<CashierAuth />} />
            <Route path="/customer/login"   element={<CustomerAuth />} />
            <Route path="/customer/register"element={<CustomerAuth />} />
            <Route path="/admin/login"      element={<AdminAuth />} />
            <Route path="/verify-otp"       element={<VerifyOTP />} />
            <Route path="/forgot-password"  element={<ForgotPassword />} />
            <Route path="/reset-password"   element={<ResetPassword />} />

            {/* ── Route aliases so ResetPassword redirects land correctly ── */}
            <Route path="/auth"          element={<Navigate to="/cashier/login" replace />} />
            <Route path="/customer/auth" element={<Navigate to="/customer/login" replace />} />

            {/* Public */}
            <Route path="/welcome"        element={<Welcome />} />
            <Route path="/privacy"         element={<WebPrivacyPolicy />} />
            <Route path="/terms"           element={<WebTermsOfService />} />
            <Route path="/delete-account"  element={<DeleteAccount />} />
            <Route path="/legal/terms"     element={<WebTermsOfService />} />
            <Route path="/legal/about"     element={<WebAbout />} />
            <Route path="/legal/contact"   element={<WebContact />} />
            <Route path="/legal/privacy"   element={<WebPrivacyPolicy />} />
            <Route path="/join/:code"     element={<JoinApp />} />

            {/* Cashier / Owner */}
            <Route path="/dashboard"           element={<OwnerRoute session={session} profile={profile}><Dashboard /></OwnerRoute>} />
            <Route path="/debtors"             element={<OwnerRoute session={session} profile={profile}><Debtors /></OwnerRoute>} />
            <Route path="/debtor/:id"          element={<OwnerRoute session={session} profile={profile}><DebtorDetail /></OwnerRoute>} />
            <Route path="/debtor/:id/history"  element={<OwnerRoute session={session} profile={profile}><DebtorHistory /></OwnerRoute>} />
            <Route path="/debtor/:id/payments" element={<OwnerRoute session={session} profile={profile}><DebtorPayments /></OwnerRoute>} />
            <Route path="/debtor/:id/add-charge" element={<OwnerRoute session={session} profile={profile}><AddCharge /></OwnerRoute>} />
            <Route path="/add-debtor"          element={<OwnerRoute session={session} profile={profile}><AddDebtor /></OwnerRoute>} />
            {/* FIX: was missing entirely — RecordPayment.js existed as a component
                and was presumably linked to from Dashboard, but had no registered
                route, so navigating to it resulted in a blank/unmatched route. */}
            <Route path="/record-payment"      element={<OwnerRoute session={session} profile={profile}><RecordPayment /></OwnerRoute>} />
            <Route path="/month-end"           element={<OwnerRoute session={session} profile={profile}><MonthEnd /></OwnerRoute>} />
            <Route path="/history"             element={<OwnerRoute session={session} profile={profile}><TransactionHistory /></OwnerRoute>} />
            <Route path="/invite"              element={<OwnerRoute session={session} profile={profile}><Invite /></OwnerRoute>} />
            <Route path="/settings"            element={<OwnerRoute session={session} profile={profile}><Settings /></OwnerRoute>} />
            <Route path="/qr"                  element={<OwnerRoute session={session} profile={profile}><QRPage /></OwnerRoute>} />
            <Route path="/profile"             element={<OwnerRoute session={session} profile={profile}><Profile /></OwnerRoute>} />
            <Route path="/notifications"       element={<OwnerRoute session={session} profile={profile}><Notifications /></OwnerRoute>} />

            {/* Admin */}
            <Route path="/admin/dashboard" element={<AdminRoute session={session} profile={profile}><AdminDashboard /></AdminRoute>} />
            <Route path="/admin/payments"  element={<AdminRoute session={session} profile={profile}><PaymentsPage /></AdminRoute>} />
            <Route path="/admin/feedback"  element={<AdminRoute session={session} profile={profile}><FeedbackPage /></AdminRoute>} />
            <Route path="/admin/accounts"  element={<AdminRoute session={session} profile={profile}><AccountsPage /></AdminRoute>} />
            <Route path="/admin/overview"  element={<AdminRoute session={session} profile={profile}><DashboardPage /></AdminRoute>} />
            <Route path="/admin/layout"    element={<AdminRoute session={session} profile={profile}><AdminLayout /></AdminRoute>} />
            <Route path="/admin/customers" element={<AdminRoute session={session} profile={profile}><CustomersPage /></AdminRoute>} />
            <Route path="/admin/reports"   element={<AdminRoute session={session} profile={profile}><ReportsPage /></AdminRoute>} />
            <Route path="/admin/activity"  element={<AdminRoute session={session} profile={profile}><ActivityPage /></AdminRoute>} />

            {/* Customer */}
            <Route path="/customer/dashboard"      element={<CustomerRoute session={session} profile={profile}><CustomerDashboard /></CustomerRoute>} />
            <Route path="/customer/restaurant/:id" element={<CustomerRoute session={session} profile={profile}><RestaurantView /></CustomerRoute>} />
            <Route path="/customer/scan"           element={<CustomerRoute session={session} profile={profile}><Scan /></CustomerRoute>} />
            <Route path="/customer/history"        element={<CustomerRoute session={session} profile={profile}><CustomerHistory /></CustomerRoute>} />
            <Route path="/customer/payments"       element={<CustomerRoute session={session} profile={profile}><CustomerPayments /></CustomerRoute>} />
            <Route path="/customer/profile"        element={<CustomerRoute session={session} profile={profile}><CustomerProfile /></CustomerRoute>} />
            <Route path="/customer/disputes"       element={<CustomerRoute session={session} profile={profile}><CustomerDispute /></CustomerRoute>} />
            <Route path="/customer/settings"       element={<CustomerRoute session={session} profile={profile}><CustomerSettings /></CustomerRoute>} />
            <Route path="/customer/notifications"  element={<CustomerRoute session={session} profile={profile}><CustomerNotifications /></CustomerRoute>} />
            <Route path="/customer/invite"         element={<CustomerRoute session={session} profile={profile}><CustomerInvite /></CustomerRoute>} />
          </Routes>
        </Suspense>
      </Router>
    </ThemeProvider>
    </AuthContext.Provider>
  );
}

export default App;